import styled from "@emotion/styled";
import { ExButton, ExCheck, useResizeObserver } from "@energyx-dev/energyx-ui";
import { pathConvertClassName } from "components/common/helper";
import { TERMS_TYPE } from "components/signup/helper";
import { DEVICE } from "constants/device";
import { hackleEventUtil } from "hackle/hackleEventUtil";
import useTranslation from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { RegisterOptions, useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { THackleClickTermsBtnProps } from "types/hackle";
import { TServices, TTerms } from "types/terms/termsTypes";

type TProps = {
  name: string;
  rules?: RegisterOptions;
  checkBoxLabel: string;
  modalNBottomSheetTitle?: string;
  termType: TTerms;
  callbackOnChange?: (checked: boolean) => void;
  size?: "default" | "small";
  signupCheckDivision?: string;
  fetchfrom?: TServices;
  joinType?: string;
};

export const AgreeCheckBoxSet = ({
  name,
  rules,
  checkBoxLabel,
  termType,
  callbackOnChange,
  size,
  signupCheckDivision = "",
  fetchfrom = "ENERGYX",
  joinType,
}: TProps) => {
  const device = useResizeObserver();
  const {
    formState: { errors },
    clearErrors,
  } = useFormContext();
  const location = useLocation();
  const { pathname } = location;

  const [checkClass, setCheckClass] = useState("");
  const [termsClass, setTermsClass] = useState("");

  const isEnglishPage = useTranslation();

  const handleOnClickButton = () => {
    hackleEventUtil<THackleClickTermsBtnProps>({
      key: "click_terms_btn",
      properties: { terms_type: termType },
    });
    if (joinType === "JOIN") {
      return window.open(
        `/terms?fetchfrom=ENERGYX&tabValue=PRIVACY_USE_AGREEMENT`,
        "_blank",
        "noopener,noreferrer"
      );
    } else {
      window.open(
        `/terms?fetchfrom=${fetchfrom}&tabValue=${termType}`,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  useEffect(() => {
    pathConvertClassName({
      pathname,
      signupCheckDivision,
      setCheckClass,
      setTermsClass,
    });
  }, [pathname]);

  useEffect(() => {
    return () => {
      setCheckClass("");
      setTermsClass("");
    };
  }, []);

  return (
    <Layout>
      <CheckBoxSet isEngPage={isEnglishPage}>
        <ExCheck
          name={name}
          size={size ? size : device === DEVICE.DESKTOP ? "default" : "small"}
          label={checkBoxLabel}
          callbackOnChange={async (checked) => {
            if (callbackOnChange) {
              callbackOnChange(checked);
            }
            if (checked) {
              clearErrors(name);
            }
          }}
          className={checkClass}
          rules={rules}
        />
        {termType !== TERMS_TYPE.NONE && (
          <ExButton
            size="small"
            variant="bordered"
            label={isEnglishPage ? "Full Terms" : "약관 전문"}
            className={termsClass}
            onClick={handleOnClickButton}
          />
        )}
      </CheckBoxSet>
      {errors.agree && (
        <ErrorCaption>
          <span className="xicon-">attention</span>
          {isEnglishPage ? "Please accept our terms and conditions." : "약관에 동의해주세요."}
        </ErrorCaption>
      )}
    </Layout>
  );
};
const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const CheckBoxSet = styled.div<{ isEngPage: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  label {
    flex: 1;
  }

  ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
    gap: ${({ isEngPage }) => (isEngPage ? "8px" : "16px")};
    flex-direction: ${({ isEngPage }) => (isEngPage ? "column" : "")};
    justify-content: ${({ isEngPage }) => (isEngPage ? "center" : "space-between")};
    align-items: ${({ isEngPage }) => (isEngPage ? "start" : "center")};

    > div:first-of-type {
      margin-left: ${({ isEngPage }) => (isEngPage ? "48px" : "")};
    }
  }
`;

const ErrorCaption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background-color: ${({ theme }) => theme.color.red[10]};
  border-radius: 8px;

  .xicon- {
    font-size: 20px;
    color: ${({ theme }) => theme.color.red[60]};
  }
  p {
    ${({ theme }) => theme.font.Caption};
    color: ${({ theme }) => theme.color.red[60]};
  }
`;
